import { inject, Injectable, isDevMode, signal } from '@angular/core';
import { catchError, EMPTY, forkJoin, Observable, tap } from 'rxjs';

import type { Fuel } from '~models/fuel.model';
import type { Service } from '~models/service.model';
import { StationDataService } from '~services/station-data.service';
import { BusinessUnit } from '~shared/business-unit';
import { getCountryFromBu, getRegionFromBu } from '~utils/business-unit.utils';
import { sentryError } from '~utils/sentry-error';

@Injectable({
  providedIn: 'root',
})
export class StationDataStore {
  private readonly stationDataService = inject(StationDataService);
  private readonly _fuels = signal<Fuel[]>([]);
  private readonly _services = signal<Service[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<unknown>(null);
  private readonly _currentBu = signal<BusinessUnit | null>(null);
  readonly fuels = this._fuels.asReadonly();
  readonly services = this._services.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly currentBu = this._currentBu.asReadonly();

  loadFuelsAndServices(bu: BusinessUnit): Observable<unknown> {
    if (this.shouldBlockLoadFuelsAndServices(bu)) {
      return EMPTY;
    }

    this._loading.set(true);
    this._error.set(null);

    const region = getRegionFromBu(bu);
    const country = getCountryFromBu(bu);

    return forkJoin({
      fuels: this.stationDataService.getFuels(country, region),
      services: this.stationDataService.getServices(country, region),
    }).pipe(
      tap(({ fuels, services }) => {
        this._fuels.set(fuels || []);
        this._services.set(services || []);
        this._loading.set(false);
        this._error.set(null);
        this._currentBu.set(bu);

        if (isDevMode()) {
          console.log('[StationDataStore] Data saved successfully!    - Current state:', {
            fuels: this._fuels(),
            services: this._services(),
            loading: this._loading(),
            error: this._error(),
            currentBu: this._currentBu(),
          });
        }
      }),
      catchError((error) => {
        sentryError(error, '[StationDataStore] API calls failed:', error.url, { region, country });
        this._loading.set(false);
        this._error.set(error);

        return EMPTY;
      }),
    );
  }

  private shouldBlockLoadFuelsAndServices(bu: BusinessUnit): boolean {
    if (bu === BusinessUnit.Mock) {
      return true;
    }

    return this._currentBu() === bu && this._fuels().length > 0 && this._services().length > 0;
  }
}
